import { useFetch } from "~vue/api";
import type { DecoratedPerformanceReview, PerformancePickerResponse } from "~vue/types/performance_management";
import { computed, type Ref } from "vue";

import { decorateReviews } from "./useReviewGroupStatus";

/**
 * Fetches the aggregate in-chat PM picker payload (moment summaries + the selected moment's
 * reviews/relationships/conversations) and exposes the reviews pre-joined for rendering. Statuses
 * are always fetched live so Start/Resume/Started labels stay correct when a picker is re-rendered
 * from history.
 *
 * Pass `momentId` to scope the detail payload to a specific moment; refetch by calling `execute()`
 * after changing it (explicit execute — not a reactive refetch — so wizard interactions can't
 * double-fire into the endpoint's 20/min throttle).
 */
export function usePerformancePicker(momentId?: Ref<number | null>) {
    const url = computed(() => {
        const id = momentId?.value;
        return id == null ? "/api/coach/performance_reviews/picker/" : `/api/coach/performance_reviews/picker/?performance_moment_id=${id}`;
    });
    const { data, error, isFetching, execute } = useFetch(url, { immediate: false }).get().json<PerformancePickerResponse>();

    const decoratedReviews = computed<DecoratedPerformanceReview[]>(() => {
        if (!data.value?.moment) {
            return [];
        }
        return decorateReviews(data.value.reviews, data.value.moment.ctas, data.value.relationships, data.value.conversations);
    });

    return { data, error, isFetching, execute, decoratedReviews };
}
